import { useEffect } from 'react'

type SEOOptions = {
  title: string
  description: string
  /** Optional path (e.g. "/repertoire") used for canonical/og:url. */
  path?: string
  /** Optional image used for og:image / twitter:image. */
  image?: string
}

const SITE_NAME = 'Tuesday – Liveband aus Kempten im Allgäu'
const DEFAULT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/7Nb4VgUjgYiNyEG22BNfBK/ChatGPT_Image_May_13,_2026,_02_14_47_PM_(1).png'

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (typeof document === 'undefined') return
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  if (typeof document === 'undefined') return
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    document.head.appendChild(tag)
  }
  tag.setAttribute('href', href)
}

export function useSEO({ title, description, path, image }: SEOOptions) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.title = title

    upsertMeta('name', 'description', description)
    upsertMeta(
      'name',
      'keywords',
      'Liveband Kempten, Coverband Allgäu, Band buchen Allgäu, Hochzeitsband Kempten, Partyband Allgäu, Tuesday Band, Liveband Süddeutschland, Live Musik Kempten',
    )

    // Open Graph
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:locale', 'de_DE')
    upsertMeta('property', 'og:image', image || DEFAULT_IMAGE)

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', image || DEFAULT_IMAGE)

    // Canonical (best-effort, based on current origin + path)
    if (path && typeof window !== 'undefined') {
      const canonical = `${window.location.origin}${path}`
      upsertLink('canonical', canonical)
      upsertMeta('property', 'og:url', canonical)
    }

    // Language
    if (document.documentElement && !document.documentElement.lang) {
      document.documentElement.lang = 'de'
    }
  }, [title, description, path, image])
}
